import React from "react";
import { Icon } from "../icons";
import { fmtDate, fmtDateTime } from "../lib/format";
import { money } from "../lib/hourly";
import { shuttleOf } from "../lib/selectors";
import {
  ReadField,
  S2Field,
  S2FieldYN,
  SectionLabel,
  TRANSIT_PAYMENT_METHODS,
  makeS2Setter,
} from "./fields";
import { DeliveryTypeField, EtaCommunication, SitLocalSection } from "./SitLocalSection";

/**
 * LD "In Transit" tab (s4t), laid out as the design of 2026-10-24 (Asana 1219846172081510):
 * delivery address and type, window, Into Trailer, ETA Communication, SIT Local with the
 * Operations check-in, the delivery checklist and payment. The footer carries
 * "SIT Local Confirmed" and "Move to Delivery Day in Progress".
 */
export const TabInTransit = ({ job, actions }) => {
  const set = makeS2Setter(job, actions.setS2Note);
  const shuttle = shuttleOf(job);
  const shuttleRate = (job.s2Notes && job.s2Notes.shuttleRate) || (shuttle.zohoRate != null ? money(shuttle.zohoRate) : "");
  const openBalance = job.openBalance != null ? job.openBalance : Math.round(job.price * 0.4);
  const toggleYN = (k, v) => actions.toggleYN(job.id, k, v);

  return (
    <>
      <SectionLabel icon="activity">Delivery Address</SectionLabel>
      <div className="field">
        <div className="val">{job.moveTo}</div>
      </div>
      <DeliveryTypeField job={job} actions={actions} />

      <SectionLabel icon="truck">Delivery Window</SectionLabel>
      <div className="field-row">
        <ReadField label="Delivery window start">{fmtDate(job.deliveryWindowStart)}</ReadField>
        <ReadField label="Delivery window end">{fmtDate(job.deliveryWindowEnd)}</ReadField>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Shuttle Included</label>
          <div className="val" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {shuttle.included ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--success)", fontWeight: 700 }}>
                <Icon name="check" /> Yes{shuttleRate ? " · " + shuttleRate : ""}
              </span>
            ) : (
              <span style={{ color: "var(--ink-500)" }}>{job.shuttleIncluded || "Not specified"}</span>
            )}
          </div>
        </div>
      </div>

      {/* Is the shipment really on the road? The warehouse's Into Trailer report says so; the
          delivery window alone does not (Miles, Asana 1219608804167658). */}
      <SectionLabel icon="truck">Into Trailer</SectionLabel>
      <div className="field-row">
        <div className="field">
          <label>Into trailer report populated</label>
          <div className="val" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {job.intoTrailerAt ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--success)", fontWeight: 700 }}>
                <Icon name="check" /> Yes · {fmtDateTime(job.intoTrailerAt)}
              </span>
            ) : (
              <span style={{ color: "var(--warning)", fontWeight: 700 }}>No — not loaded on the trailer yet</span>
            )}
          </div>
        </div>
      </div>

      <EtaCommunication job={job} actions={actions} key={`eta-${job.id}`} />

      <SitLocalSection job={job} actions={actions} key={`sit-${job.id}`} />

      <div className="section-label">
        <Icon name="clipboard" />
        Delivery Checklist{" "}
        <span className="meta" style={{ textTransform: "none", fontWeight: 600, letterSpacing: 0, color: "var(--ink-400)" }}>
          — all optional
        </span>
      </div>
      <div className="field-row">
        <S2FieldYN
          job={job}
          fieldKey="deliveryElevatorRestriction"
          label="Elevator restriction at delivery?"
          hint={"Earlier note: " + ((job.s2Notes && job.s2Notes.deliveryAccess) || "none")}
          onChange={set}
          onToggleYN={toggleYN}
        />
        <S2FieldYN job={job} fieldKey="unpackingIncluded" label="Unpacking service included?" onChange={set} onToggleYN={toggleYN} />
      </div>
      <div className="field-row">
        <S2FieldYN
          job={job}
          fieldKey="coiCompleted"
          label="COI completed?"
          hint={"Earlier note: " + ((job.s2Notes && job.s2Notes.coi) || "none")}
          onChange={set}
          onToggleYN={toggleYN}
        />
        <S2FieldYN
          job={job}
          fieldKey="contractSignedCheck"
          label="Contract signed?"
          hint={"Zoho status: " + (job.contractSigned ? "Signed" : "Not signed")}
          onChange={set}
          onToggleYN={toggleYN}
        />
      </div>

      <SectionLabel icon="check">Payment Details</SectionLabel>
      <div className="field-row">
        <ReadField label="Open balance">${openBalance}</ReadField>
        <div className="field">
          <label>Preferred way of payment</label>
          <select
            value={job.transitPaymentMethod || ""}
            onChange={(e) => actions.setJobField(job.id, "transitPaymentMethod", e.target.value)}
          >
            {TRANSIT_PAYMENT_METHODS.map(([v, l]) => (
              <option value={v} key={v || "none"}>
                {l}
              </option>
            ))}
          </select>
        </div>
      </div>
      <S2Field job={job} fieldKey="paymentPreferenceNote" label="Note about preferred payment (optional)" onChange={set} />
    </>
  );
};
